import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'wouter';
import {
  Search,
  LayoutDashboard,
  FolderOpen,
  Clock,
  Archive,
  FileText,
  Tag,
  BarChart2,
  Settings,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onAskEdison: (query: string) => void;
  initialQuery?: string;
}

const NAV_SHORTCUTS = [
  { label: 'Home', href: '/', icon: LayoutDashboard, sublabel: 'Dashboard & decisions' },
  { label: 'Insights', href: '/insights', icon: BarChart2, sublabel: 'R&D trends & velocity' },
  { label: 'Projects', href: '/projects', icon: FolderOpen, sublabel: 'TFA, activities & evidence' },
  { label: 'Time', href: '/timewriting', icon: Clock, sublabel: 'Review & classify time entries' },
  { label: 'Evidence', href: '/artefacts', icon: Archive, sublabel: 'Artefacts, signals & links' },
  { label: 'Claims', href: '/claims', icon: FileText, sublabel: 'Assemble, lock & export' },
  { label: 'Audit Readiness', href: '/audit-readiness', icon: ShieldCheck, sublabel: 'Eligibility evidence report' },
  { label: 'Grants', href: '/grants', icon: Tag, sublabel: 'Discover & match funding' },
  { label: 'Settings', href: '/integrations', icon: Settings, sublabel: 'Integrations, users & rates' },
];

export function CommandPalette({ open, onClose, onAskEdison, initialQuery = '' }: CommandPaletteProps) {
  const [query, setQuery] = useState(initialQuery);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setActiveIdx(0);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 30);
    }
  }, [open, initialQuery]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => i + 1); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(0, i - 1)); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const filtered = query
    ? NAV_SHORTCUTS.filter(
        item =>
          item.label.toLowerCase().includes(query.toLowerCase()) ||
          item.sublabel.toLowerCase().includes(query.toLowerCase()),
      )
    : NAV_SHORTCUTS;

  const totalRows = (query ? 1 : 0) + filtered.length;
  const clampedIdx = Math.min(activeIdx, Math.max(0, totalRows - 1));

  const handleSelect = useCallback(
    (href: string) => {
      navigate(href);
      onClose();
    },
    [navigate, onClose],
  );

  const handleAskEdison = useCallback(() => {
    onAskEdison(query);
    onClose();
  }, [onAskEdison, query, onClose]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (query && clampedIdx === 0) {
        handleAskEdison();
      } else {
        const itemIdx = query ? clampedIdx - 1 : clampedIdx;
        if (filtered[itemIdx]) handleSelect(filtered[itemIdx].href);
      }
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-foreground/25 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Palette card */}
      <div
        className="relative w-full max-w-[620px] mx-4 bg-card rounded-2xl overflow-hidden border border-border"
        style={{ boxShadow: '0 24px 64px oklch(0.15 0.02 250 / 0.22), 0 4px 12px oklch(0.15 0.02 250 / 0.10)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search or ask Edison…"
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
            aria-label="Command palette search"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border font-mono leading-none">
            Esc
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[400px] overflow-y-auto py-1.5">
          {/* Ask Edison row — first when query is present */}
          {query && (
            <button
              onClick={handleAskEdison}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${clampedIdx === 0 ? 'bg-secondary' : 'hover:bg-secondary/60'}`}
            >
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: 'linear-gradient(150deg, oklch(0.44 0.09 262), oklch(0.30 0.07 264))' }}
                aria-hidden="true"
              >
                <Sparkles className="h-4 w-4 text-primary-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate">Ask Edison: "{query}"</p>
                <p className="text-xs text-muted-foreground">Open assistant with this question</p>
              </div>
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border font-mono shrink-0 leading-none">
                Ask
              </span>
            </button>
          )}

          {/* Nav shortcuts */}
          {filtered.length > 0 ? (
            <div>
              {!query && (
                <p className="px-4 pt-2 pb-1 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                  Navigate
                </p>
              )}
              {filtered.map((item, i) => {
                const rowIdx = query ? i + 1 : i;
                return (
                  <button
                    key={item.href}
                    onClick={() => handleSelect(item.href)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${clampedIdx === rowIdx ? 'bg-secondary' : 'hover:bg-secondary/60'}`}
                  >
                    <div className="h-8 w-8 rounded-lg bg-secondary flex items-center justify-center shrink-0" aria-hidden="true">
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">{item.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{item.sublabel}</p>
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">Go</span>
                  </button>
                );
              })}
            </div>
          ) : (
            query && (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">No matches for "{query}"</p>
                <p className="text-xs text-muted-foreground mt-1">Press Enter to ask Edison</p>
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border px-4 py-2 flex items-center justify-between bg-secondary/30">
          <p className="text-xs text-muted-foreground">Everything is ≤ 2 keystrokes away</p>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <kbd className="bg-muted px-1.5 py-0.5 rounded border border-border font-mono leading-none">↑</kbd>
            <kbd className="bg-muted px-1.5 py-0.5 rounded border border-border font-mono leading-none">↓</kbd>
            <span className="ml-1">navigate</span>
            <kbd className="ml-2 bg-muted px-1.5 py-0.5 rounded border border-border font-mono leading-none">↵</kbd>
            <span>select</span>
          </div>
        </div>
      </div>
    </div>
  );
}
